import * as React from 'react';
import Box from '@mui/material/Box';
import { Grid, TextField, Theme, Tooltip, useTheme } from '@mui/material';
import FileUpload from '../../shared/input/file/FileUpload';
import { ReactComponent as CompanyIcon } from '../../../assets/icons/Company_Organisation.svg';
import { Company, Group, Standard } from '@esg/esg-global-types';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { CompanyExtended } from '../../../lib/app/company';
import { hasBannedCharacters } from '../../../lib/validation/text_validation';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { AxiosResponse } from 'axios';
import StandardSelect from '../../shared/input/select/StandardSelect';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { Heading } from '../../shared/typography/PrimaryTypography';
import { default_subscription_key_values } from '../../../lib/app/subscription';
import { createCompany } from '../../../graphql/services/company.service';
import { CreateCompanyClientData } from '@esg/syntiro-api-core';

/**
 * Widget for creating new company entities for a group in the app
 * @param {Group} group Group to create new Company under
 * @param {void} handleCompanyToParent Use the new company data in a callback function
 * @returns {JSX.Element}
 */
const AddCompanyWidget = ({
  group,
  handleCompanyToParent
}: {
  group: Group;
  handleCompanyToParent: (new_company: CompanyExtended) => void;
}) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [companyName, setCompanyName] = React.useState('');
  const [logoFilePath, setLogoFilePath] = React.useState('');
  const [standardList, setStandardList] = React.useState(Array<Standard>);
  const [addLoading, setAddLoading] = React.useState<boolean>(false);
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const invalidCompanyName = companyName ? hasBannedCharacters(companyName) : false;

  const handleCreateCompany = async () => {
    try {
      if (companyName !== '') {
        setAddLoading(true);
        const create_company_client_data: CreateCompanyClientData = {
          active: true,
          name: companyName,
          standards: standardList,
          configuration: {
            logo: logoFilePath
          },
          client_bigquery_dataset: '',
          client_bigquery_dataset_sync: false,
          subscription: {
            quota: {},
            usage: {}
          }
        };
        const new_company: Company | null | undefined = await createCompany(
          { group_id: group.id },
          create_company_client_data,
          {
            create_audit_log: true,
            return_data: true
          }
        ).catch((error) => {
          throw new Error(error);
        });
        if (new_company) {
          handleCompanyToParent({
            id: new_company.id,
            name: companyName,
            active: true,
            deleted: null,
            configuration: {
              logo: logoFilePath
            },
            client_bigquery_dataset: '',
            client_bigquery_dataset_sync: false,
            subscription: {
              quota: default_subscription_key_values,
              usage: default_subscription_key_values
            },
            group: {
              id: group.id,
              deleted: null,
              name: group.name,
              active: group.active,
              sectors: group.sectors,
              enterprise_classification: group.enterprise_classification,
              guided_readiness: group.guided_readiness
            }
          });
        }
        setAddLoading(false);
        setCompanyName('');
        setLogoFilePath('');
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: AddCompanyWidget failed on an unknown error while calling handleCreateCompany.',
          {
            companyName: companyName
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Error creating company. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setAddLoading(false);
    }
  };

  const logoFilePathFromChild = (uploaded_file: AxiosResponse) => {
    setLogoFilePath(uploaded_file.data.url);
  };

  const standardListFromChild = (standards: React.SetStateAction<Array<Standard>>) => {
    setStandardList(standards);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={4}>
        <Grid size={10}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            <CompanyIcon height={40} fill={primary_blue} stroke={primary_blue} />
            <Heading sx={{ mx: 2 }}>Add Company</Heading>
          </Box>
        </Grid>
        <Grid size={12}>
          <TextField
            error={invalidCompanyName}
            fullWidth
            id="company-name-input"
            label="Name"
            variant="standard"
            value={companyName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              setCompanyName(event.target.value);
            }}
          />
        </Grid>

        <Grid size={12}>
          <StandardSelect
            master_list
            multi_select
            handleChangeStandards={(standards: Standard | Array<Standard> | null) => {
              if (Array.isArray(standards)) {
                standardListFromChild(standards);
              }
            }}
          />
        </Grid>

        <Grid size={12}>
          <Tooltip title="Upload company logo">
            <Box>
              <FileUpload
                uploadedFileToParent={logoFilePathFromChild}
                file_type={'configuration'}
                company_name={companyName}
                group_id={group.id}
              />
            </Box>
          </Tooltip>
        </Grid>

        <Grid size={12} alignItems={'right'}>
          <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
            <PrimaryButton
              loading={addLoading}
              disabled={!companyName || invalidCompanyName}
              onClick={handleCreateCompany}
            >
              Create
            </PrimaryButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddCompanyWidget;
